import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { ALL_DEPARTMENTS, DepartmentToggle } from "@/components/equipment/DepartmentToggle";
import { CategoryMultiFilter } from "@/components/equipment/CategoryMultiFilter";
import { OccupancyGrid } from "@/components/equipment/OccupancyGrid";
import { OccupancyMobileList } from "@/components/equipment/OccupancyMobileList";
import { BookingDialog, type BookingDialogDefaults } from "@/components/equipment/BookingDialog";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentCategories, useEquipmentList } from "@/hooks/use-equipment";
import { useEquipmentOccupancy } from "@/hooks/use-equipment-bookings";
import { addDays } from "@/lib/equipment/availability";
import { todayIso } from "@/lib/equipment/insurance";
import { groupByDepartment, isoWeek, occupancyDays, occupancyFetchRange, showsInOccupancy, weekStart } from "@/lib/equipment/occupancy";

const GRID_DAYS = 14;

/** /utstyr/belegg (VIB-2920, mockup G): bookbart utstyr × dager, to uker om gangen. */
export default function EquipmentOccupancyPage() {
  return (
    <EquipmentFlagGate>
      <Occupancy />
    </EquipmentFlagGate>
  );
}

function Occupancy() {
  const today = todayIso();
  const access = useEquipmentAccess();
  const { data: departments = [] } = useDepartments();
  const { data: categories = [] } = useEquipmentCategories();
  const { data: items = [], isLoading, error } = useEquipmentList(access.canManage, !access.isLoading);
  const [start, setStart] = useState(() => weekStart(today));
  const [department, setDepartment] = useState<string>(ALL_DEPARTMENTS);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [dialog, setDialog] = useState<{ bookingId: string | null; defaults?: BookingDialogDefaults } | null>(null);

  const range = useMemo(() => occupancyFetchRange(start, GRID_DAYS, today), [start, today]);
  const { data: lines = [], error: linesError } = useEquipmentOccupancy(range);
  const days = useMemo(() => occupancyDays(start, GRID_DAYS), [start]);

  const categoryBookable = useMemo(() => new Map(categories.map((c) => [c.id, c.bookable])), [categories]);
  const visible = useMemo(
    () =>
      items.filter(
        (i) =>
          showsInOccupancy(i, categoryBookable) &&
          (department === ALL_DEPARTMENTS || i.department_id === department) &&
          (categoryIds.length === 0 || categoryIds.includes(i.category_id)),
      ),
    [items, categoryBookable, department, categoryIds],
  );
  const groups = useMemo(() => groupByDepartment(visible, departments), [visible, departments]);
  const visibleLines = useMemo(() => {
    const ids = new Set(visible.map((i) => i.id));
    return lines.filter((l) => ids.has(l.item_id));
  }, [lines, visible]);

  const firstWeek = isoWeek(start);
  const lastWeek = isoWeek(addDays(start, GRID_DAYS - 1));
  const openBooking = (bookingId: string) => setDialog({ bookingId });

  return (
    <div className="page-container animate-fade-in space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/utstyr" aria-label="Tilbake til utstyr">
              <SrIcon name="chevron-left" className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
            <SrIcon name="calendar" className="h-6 w-6" />
            Belegg
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DepartmentToggle departments={departments} value={department} onChange={setDepartment} />
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="icon" aria-label="Forrige uke" onClick={() => setStart(addDays(start, -7))}>
              <SrIcon name="chevron-left" className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium tabular-nums">Uke {firstWeek}–{lastWeek}</span>
            <Button variant="outline" size="icon" aria-label="Neste uke" onClick={() => setStart(addDays(start, 7))}>
              <SrIcon name="chevron-right" className="h-4 w-4" />
            </Button>
            {start !== weekStart(today) && (
              <Button variant="ghost" size="sm" onClick={() => setStart(weekStart(today))}>I dag</Button>
            )}
          </div>
          <CategoryMultiFilter categories={categories.filter((c) => c.bookable)} selected={categoryIds} onChange={setCategoryIds} />
        </div>
      </div>
      <Card>
        <CardContent className="pt-6">
          {error || linesError ? (
            <p className="text-destructive">Feil ved lasting av belegget: {((error ?? linesError) as Error).message}</p>
          ) : isLoading || access.isLoading ? (
            <div className="flex justify-center py-8">
              <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="md:hidden">
                <OccupancyMobileList lines={visibleLines} today={today} onOpenBooking={openBooking} />
              </div>
              <div className="hidden md:block space-y-3">
                {groups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ingen bookbart utstyr med dette filteret.</p>
                ) : (
                  <OccupancyGrid
                    groups={groups}
                    days={days}
                    lines={visibleLines}
                    today={today}
                    onOpenBooking={openBooking}
                    onBookFree={(itemId, day) => setDialog({ bookingId: null, defaults: { itemId, dateFrom: day } })}
                  />
                )}
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span><span className="rounded bg-primary px-1.5 py-0.5 text-primary-foreground">631</span> Booket (prosjektnr)</span>
                  <span><span className="rounded bg-destructive px-1.5 py-0.5 text-destructive-foreground">⚠</span> Dobbeltbooket</span>
                  <span>▨ Service</span>
                  <span>Tall = antall booket av mengdevare</span>
                  <span>Klikk på en ledig dag for å booke</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <BookingDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        bookingId={dialog?.bookingId ?? null}
        defaults={dialog?.defaults}
      />
    </div>
  );
}
